import type { MutableRefObject } from "react";
import { useCallback } from "react";
import type { OptionsOrGroups } from "react-select";

import type { GroupBase } from "@braze/beacon-lab-select";

type LoadOptionsInvoker<OptionType> = (
  inputValue: string,
  callback: (options: OptionsOrGroups<OptionType, GroupBase<OptionType>>) => void
) => void | PromiseLike<OptionsOrGroups<OptionType, GroupBase<OptionType>>>;

const isOptionGroup = <OptionType>(
  optionOrGroup: OptionType | GroupBase<OptionType>
): optionOrGroup is GroupBase<OptionType> => {
  return (
    typeof optionOrGroup === "object" &&
    optionOrGroup !== null &&
    "options" in optionOrGroup &&
    Array.isArray((optionOrGroup as { options?: unknown }).options)
  );
};

const flattenOptions = <OptionType>(
  optionsOrGroups: OptionsOrGroups<OptionType, GroupBase<OptionType>>
): OptionType[] => {
  return optionsOrGroups.flatMap((optionOrGroup) => {
    if (isOptionGroup(optionOrGroup)) {
      return [...optionOrGroup.options];
    }

    return [optionOrGroup];
  });
};

/**
 * Wraps react-select async `loadOptions` (callback + optional promise) as a single async search function
 * for Operator tool registration.
 */
export function useAsyncLoadOptionsSearchFn<OptionType>(
  loadOptionsRef: MutableRefObject<LoadOptionsInvoker<OptionType> | undefined>
): (searchQuery: string) => Promise<OptionType[]> {
  return useCallback(
    async (searchQuery: string): Promise<OptionType[]> => {
      const loadOptionsFn = loadOptionsRef.current;
      if (!loadOptionsFn) {
        throw new Error("loadOptions is not defined");
      }

      const result = await new Promise<OptionsOrGroups<OptionType, GroupBase<OptionType>>>((resolve, reject) => {
        const maybePromise = loadOptionsFn(searchQuery, (options) => {
          resolve(options);
        });

        if (maybePromise && typeof maybePromise === "object" && "then" in maybePromise) {
          (maybePromise as Promise<OptionsOrGroups<OptionType, GroupBase<OptionType>>>).then(resolve).catch(reject);
        }
      });

      return flattenOptions(result);
    },
    [loadOptionsRef]
  );
}
